import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import cssLang from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import { Braces, Code2, FileCode2, Palette, Sparkles } from 'lucide-react';
import { formatCode } from './codeFormat';
import {
  getPartLineCount,
  type TemplateEditorTab,
  type TemplateParts,
} from './templateParts';
import './reportCodeEditor.css';

SyntaxHighlighter.registerLanguage('markup', markup);
SyntaxHighlighter.registerLanguage('css', cssLang);
SyntaxHighlighter.registerLanguage('javascript', javascript);

export interface ReportCodeEditorHandle {
  insertAtCursor: (text: string) => void;
  focus: () => void;
  getParts: () => TemplateParts;
}

interface ReportCodeEditorProps {
  theme?: 'dark' | 'light';
  parts: TemplateParts;
  onChange: (parts: TemplateParts) => void;
}

const TAB_DOT: Record<TemplateEditorTab, string> = {
  html: 'bg-orange-500',
  css: 'bg-sky-500',
  js: 'bg-amber-500',
};

const TAB_CONFIG: Record<
  TemplateEditorTab,
  { label: string; prismLanguage: string; icon: React.ElementType; accent: string; desc: string }
> = {
  html: {
    label: 'HTML',
    prismLanguage: 'markup',
    icon: FileCode2,
    accent: 'text-orange-600',
    desc: 'Markup & structure',
  },
  css: {
    label: 'CSS',
    prismLanguage: 'css',
    icon: Palette,
    accent: 'text-sky-600',
    desc: 'Styles & layout',
  },
  js: {
    label: 'JavaScript',
    prismLanguage: 'javascript',
    icon: Braces,
    accent: 'text-amber-700',
    desc: 'Interactivity & logic',
  },
};

const EDITOR_FONT =
  "'JetBrains Mono', 'Cascadia Code', 'Fira Code', 'Consolas', 'Courier New', monospace";
const LINE_HEIGHT = 21;
const FONT_SIZE = 13;
const PADDING = 12;
const TAB_SPACES = '  ';
const PARENT_SYNC_DELAY_MS = 300;

const lineStyle = `${LINE_HEIGHT}px`;

const EDITOR_COLORS = {
  bg: '#ffffff',
  text: '#1e293b',
  gutterBg: '#f8fafc',
  gutterText: '#94a3b8',
  gutterBorder: '#e2e8f0',
  caret: '#4f46e5',
  lineHighlight: '#f1f5f9',
};

const editorTextStyle: React.CSSProperties = {
  fontFamily: EDITOR_FONT,
  fontSize: FONT_SIZE,
  lineHeight: lineStyle,
  tabSize: 2,
  padding: PADDING,
  margin: 0,
  boxSizing: 'border-box',
  whiteSpace: 'pre',
  overflowWrap: 'normal',
  wordWrap: 'normal',
};

const highlighterCustomStyle: React.CSSProperties = {
  fontFamily: EDITOR_FONT,
  fontSize: FONT_SIZE,
  lineHeight: lineStyle,
  tabSize: 2,
  padding: PADDING,
  margin: 0,
  boxSizing: 'border-box',
  whiteSpace: 'pre',
  background: EDITOR_COLORS.bg,
  overflow: 'visible',
  minHeight: '100%',
};

const highlighterCodeStyle: React.CSSProperties = {
  fontFamily: EDITOR_FONT,
  fontSize: FONT_SIZE,
  lineHeight: lineStyle,
  tabSize: 2,
  margin: 0,
  padding: 0,
  display: 'block',
  background: 'transparent',
  color: EDITOR_COLORS.text,
  whiteSpace: 'pre',
  overflowWrap: 'normal',
  wordWrap: 'normal',
};

const highlighterLineStyle: React.CSSProperties = {
  display: 'block',
  lineHeight: lineStyle,
  minHeight: lineStyle,
  height: lineStyle,
};

function getLineCol(code: string, pos: number): { line: number; column: number } {
  const before = code.slice(0, pos);
  const lines = before.split('\n');
  return { line: lines.length, column: (lines[lines.length - 1]?.length ?? 0) + 1 };
}

function getLineCount(code: string): number {
  if (!code) return 1;
  return code.split('\n').length;
}

function useDebouncedCallback<T extends (arg: TemplateParts) => void>(fn: T, delay: number) {
  const fnRef = useRef(fn);
  const timerRef = useRef<number | null>(null);
  const pendingRef = useRef<TemplateParts | null>(null);

  fnRef.current = fn;

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const flush = useCallback(() => {
    if (pendingRef.current === null) return;
    cancel();
    const next = pendingRef.current;
    pendingRef.current = null;
    fnRef.current(next);
  }, [cancel]);

  const schedule = useCallback(
    (parts: TemplateParts) => {
      pendingRef.current = parts;
      cancel();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        if (pendingRef.current !== null) {
          fnRef.current(pendingRef.current);
          pendingRef.current = null;
        }
      }, delay);
    },
    [cancel, delay]
  );

  useEffect(() => cancel, [cancel]);

  return { schedule, flush, cancel };
}

interface IdeEditorPaneProps {
  value: string;
  language: string;
  activeLine: number;
  onChange: (value: string) => void;
  onCursorChange: (line: number, column: number) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

const IdeEditorPane = memo(function IdeEditorPane({
  value,
  language,
  activeLine,
  onChange,
  onCursorChange,
  textareaRef,
}: IdeEditorPaneProps) {
  const gutterRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const lineCount = getLineCount(value);
  const lines = useMemo(
    () => Array.from({ length: lineCount }, (_, i) => i + 1),
    [lineCount]
  );

  const displayCode = value.endsWith('\n') ? value : `${value}\n`;

  const syncScroll = (scrollTop: number, scrollLeft: number) => {
    if (gutterRef.current) gutterRef.current.scrollTop = scrollTop;
    if (highlightRef.current) {
      highlightRef.current.scrollTop = scrollTop;
      highlightRef.current.scrollLeft = scrollLeft;
    }
  };

  const syncCursor = useCallback(
    (el: HTMLTextAreaElement) => {
      const { line, column } = getLineCol(value, el.selectionStart);
      onCursorChange(line, column);
    },
    [onCursorChange, value]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;

    if (e.key === 'Tab') {
      e.preventDefault();
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = value.slice(0, start) + TAB_SPACES + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = start + TAB_SPACES.length;
        syncCursor(el);
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const before = value.slice(0, start);
      const lineStart = before.lastIndexOf('\n') + 1;
      const currentLine = before.slice(lineStart);
      const indentMatch = currentLine.match(/^[\t ]*/);
      const indent = indentMatch?.[0] ?? '';
      const extra = currentLine.trimEnd().endsWith('{') ? TAB_SPACES : '';
      const insert = `\n${indent}${extra}`;
      const next = value.slice(0, start) + insert + value.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        const pos = start + insert.length;
        el.selectionStart = el.selectionEnd = pos;
        syncCursor(el);
      });
    }
  };

  return (
    <div
      className="report-code-editor flex flex-1 min-h-0 overflow-hidden"
      style={{ backgroundColor: EDITOR_COLORS.bg, isolation: 'isolate' }}
    >
      <div
        ref={gutterRef}
        className="flex-shrink-0 select-none text-right overflow-hidden"
        style={{
          width: 44,
          paddingTop: PADDING,
          paddingBottom: PADDING,
          backgroundColor: EDITOR_COLORS.gutterBg,
          borderRight: `1px solid ${EDITOR_COLORS.gutterBorder}`,
          color: EDITOR_COLORS.gutterText,
          fontFamily: EDITOR_FONT,
          fontSize: FONT_SIZE,
        }}
      >
        {lines.map((n) => (
          <div
            key={n}
            style={{
              minHeight: LINE_HEIGHT,
              height: LINE_HEIGHT,
              lineHeight: lineStyle,
              paddingRight: 8,
              backgroundColor: n === activeLine ? EDITOR_COLORS.lineHighlight : 'transparent',
              color: n === activeLine ? '#475569' : EDITOR_COLORS.gutterText,
              fontWeight: n === activeLine ? 600 : 400,
            }}
          >
            {n}
          </div>
        ))}
      </div>

      <div className="relative flex-1 min-w-0 overflow-hidden report-code-editor-pane">
        <div
          ref={highlightRef}
          className="report-code-highlight absolute inset-0 overflow-auto pointer-events-none"
          aria-hidden
        >
          <SyntaxHighlighter
            language={language}
            style={{}}
            useInlineStyles={false}
            wrapLines
            wrapLongLines={false}
            showLineNumbers={false}
            showInlineLineNumbers={false}
            lineProps={{ style: highlighterLineStyle, className: 'report-code-line' }}
            PreTag="div"
            CodeTag="code"
            customStyle={highlighterCustomStyle}
            codeTagProps={{ style: highlighterCodeStyle, className: 'report-code-highlight-code' }}
          >
            {displayCode}
          </SyntaxHighlighter>
        </div>

        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            syncCursor(e.target);
          }}
          onKeyDown={handleKeyDown}
          onKeyUp={(e) => syncCursor(e.currentTarget)}
          onClick={(e) => syncCursor(e.currentTarget)}
          onSelect={(e) => syncCursor(e.currentTarget)}
          onScroll={(e) => syncScroll(e.currentTarget.scrollTop, e.currentTarget.scrollLeft)}
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          className="absolute inset-0 w-full h-full resize-none outline-none border-0 overflow-auto report-code-textarea"
          style={{
            ...editorTextStyle,
            backgroundColor: 'transparent',
            color: 'transparent',
            caretColor: EDITOR_COLORS.caret,
            WebkitTextFillColor: 'transparent',
          }}
        />
      </div>
    </div>
  );
});

const ReportCodeEditor = forwardRef<ReportCodeEditorHandle, ReportCodeEditorProps>(
  ({ parts, onChange }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [activeTab, setActiveTab] = useState<TemplateEditorTab>('html');
    const [cursor, setCursor] = useState({ line: 1, column: 1 });
    const [localParts, setLocalParts] = useState(parts);
    const localPartsRef = useRef(localParts);
    localPartsRef.current = localParts;

    const { schedule: scheduleParentSync, flush: flushParentSync, cancel: cancelParentSync } =
      useDebouncedCallback(onChange, PARENT_SYNC_DELAY_MS);

    useEffect(() => {
      const current = localPartsRef.current;
      if (
        parts.html === current.html &&
        parts.css === current.css &&
        parts.js === current.js
      ) {
        return;
      }
      cancelParentSync();
      setLocalParts(parts);
    }, [parts, cancelParentSync]);

    const handleCursorChange = useCallback((line: number, column: number) => {
      setCursor((prev) => (prev.line === line && prev.column === column ? prev : { line, column }));
    }, []);

    const updatePart = useCallback(
      (tab: TemplateEditorTab, value: string) => {
        const next = { ...localPartsRef.current, [tab]: value };
        setLocalParts(next);
        scheduleParentSync(next);
      },
      [scheduleParentSync]
    );

    const insertAtCursor = useCallback(
      (text: string) => {
        const el = textareaRef.current;
        const current = localPartsRef.current[activeTab];
        if (!el) {
          updatePart(activeTab, current + text);
          return;
        }
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const next = current.slice(0, start) + text + current.slice(end);
        updatePart(activeTab, next);
        requestAnimationFrame(() => {
          el.focus();
          const pos = start + text.length;
          el.setSelectionRange(pos, pos);
          setCursor(getLineCol(next, pos));
        });
      },
      [activeTab, updatePart]
    );

    useImperativeHandle(
      ref,
      () => ({
        insertAtCursor,
        focus: () => textareaRef.current?.focus(),
        getParts: () => {
          flushParentSync();
          return localPartsRef.current;
        },
      }),
      [insertAtCursor, flushParentSync]
    );

    const insertSnippet = () => {
      const snippets: Record<TemplateEditorTab, string> = {
        html: '\n<section class="card">\n  <h2>Section Title</h2>\n  <p>{entity.field}</p>\n</section>\n',
        css: '\n.card {\n  padding: 16px;\n  border-radius: 8px;\n  border: 1px solid #e2e8f0;\n}\n',
        js: "\ndocument.addEventListener('DOMContentLoaded', () => {\n  // Your logic here\n});\n",
      };
      insertAtCursor(snippets[activeTab]);
    };

    const formatDocument = () => {
      const formatted = formatCode(localParts[activeTab], activeTab);
      updatePart(activeTab, formatted);
      requestAnimationFrame(() => textareaRef.current?.focus());
    };

    const activeConfig = TAB_CONFIG[activeTab];
    const totalLines =
      getPartLineCount(localParts.html) +
      getPartLineCount(localParts.css) +
      getPartLineCount(localParts.js);

    return (
      <div
        className="report-code-editor-root report-code-editor flex flex-col h-full min-h-0 border-l border-gray-200"
        style={{ backgroundColor: EDITOR_COLORS.bg, color: EDITOR_COLORS.text }}
      >
        <div className="flex-shrink-0 flex items-stretch border-b border-gray-200 bg-gray-50">
          <div
            className="flex items-center gap-1.5 px-3 border-r border-gray-200"
            style={{ backgroundColor: EDITOR_COLORS.bg, color: '#475569' }}
          >
            <Code2 className="w-3.5 h-3.5 text-indigo-600" />
            <span className="text-[10px] font-semibold uppercase tracking-wider hidden sm:inline">
              Source
            </span>
          </div>

          <div className="flex items-end flex-1 min-w-0 overflow-x-auto">
            {(Object.keys(TAB_CONFIG) as TemplateEditorTab[]).map((tab) => {
              const cfg = TAB_CONFIG[tab];
              const Icon = cfg.icon;
              const isActive = activeTab === tab;
              const lineN = getPartLineCount(localParts[tab]);

              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className="group relative flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium border-r border-gray-200 transition-colors min-w-[100px]"
                  style={{
                    backgroundColor: isActive ? EDITOR_COLORS.bg : '#f8fafc',
                    color: isActive ? '#0f172a' : '#64748b',
                  }}
                  title={cfg.desc}
                >
                  {isActive && (
                    <span className="absolute top-0 left-0 right-0 h-[2px] bg-indigo-500" />
                  )}
                  <Icon
                    className={`w-3 h-3 flex-shrink-0 ${isActive ? cfg.accent : ''}`}
                    style={!isActive ? { color: '#94a3b8' } : undefined}
                  />
                  <span>{cfg.label}</span>
                  <span
                    className="text-[9px] px-1.5 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: isActive ? '#f1f5f9' : '#e2e8f0',
                      color: isActive ? '#475569' : '#64748b',
                    }}
                  >
                    {lineN}L
                  </span>
                </button>
              );
            })}
          </div>

          <div
            className="flex items-center gap-1 px-2 border-l border-gray-200"
            style={{ backgroundColor: EDITOR_COLORS.bg }}
          >
            <button
              type="button"
              onClick={insertSnippet}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors"
              style={{ color: '#4f46e5' }}
              title="Insert snippet"
            >
              <Sparkles className="w-3 h-3" />
              Snippet
            </button>
            <button
              type="button"
              onClick={formatDocument}
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors"
              style={{ color: '#475569' }}
              title="Format & indent code"
            >
              Format
            </button>
          </div>
        </div>

        <div
          className="flex-shrink-0 flex items-center justify-between px-3 py-1.5 border-b border-gray-100 text-[10px]"
          style={{ backgroundColor: EDITOR_COLORS.bg, color: '#64748b' }}
        >
          <div className="flex items-center gap-1 font-mono truncate">
            <span style={{ color: '#94a3b8' }}>report</span>
            <span style={{ color: '#cbd5e1' }}>/</span>
            <span className={`font-semibold ${activeConfig.accent}`}>
              {activeConfig.label.toLowerCase()}
            </span>
            <span style={{ color: '#cbd5e1' }}>·</span>
            <span className="truncate">{activeConfig.desc}</span>
          </div>
          <span style={{ color: '#94a3b8' }} className="flex-shrink-0 ml-2">
            {totalLines} lines total
          </span>
        </div>

        <div className="flex-1 min-h-0 flex flex-col">
          <IdeEditorPane
            key={activeTab}
            value={localParts[activeTab]}
            language={activeConfig.prismLanguage}
            activeLine={cursor.line}
            onChange={(v) => updatePart(activeTab, v)}
            onCursorChange={handleCursorChange}
            textareaRef={textareaRef}
          />
        </div>

        <div className="report-code-statusbar flex-shrink-0 flex items-center justify-between px-3 py-1 bg-indigo-600 text-[10px] font-medium">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${TAB_DOT[activeTab]}`} />
              {activeConfig.label}
            </span>
            <span className="report-code-statusbar-muted">
              Ln {cursor.line}, Col {cursor.column}
            </span>
            <span className="report-code-statusbar-muted">Spaces: 2</span>
            <span className="report-code-statusbar-muted">UTF-8</span>
          </div>
          <div className="report-code-statusbar-muted">{getPartLineCount(localParts[activeTab])} lines</div>
        </div>
      </div>
    );
  }
);

ReportCodeEditor.displayName = 'ReportCodeEditor';

export default ReportCodeEditor;
